import { Plus, X } from 'lucide-react';
import {
  FRICTION_SIGNALS,
  MAX_TRIGGER_CONDITIONS,
  TRIGGER_KINDS,
  type ObservedElementDto,
  type TriggerCondition,
  type TriggerRule,
} from '@ederest/contracts';
import { Button } from '../ui/Button.js';
import { CONDITION_LABEL, SIGNAL_LABEL } from './labels.js';

interface TriggerEditorProps {
  value: TriggerRule;
  /** Observed elements of the step: the only targets a condition may name. */
  elements: readonly ObservedElementDto[];
  onChange: (next: TriggerRule) => void;
}

const DEFAULT_DWELL_MS = 8000;

function emptyCondition(): TriggerCondition {
  return { kind: 'on_step_enter', targetSelector: null, dwellTimeMs: null, frictionSignal: null };
}

/**
 * The composed trigger rule of technical §7.4: one to three conditions joined
 * with AND, plus the non-repetition limits of functional §6.7. Durations and
 * display counts are adjustable here; the repeated-click and dead-click
 * thresholds are fixed and appear nowhere (functional §6.3).
 */
export function TriggerEditor({ value, elements, onChange }: TriggerEditorProps) {
  const setCondition = (index: number, patch: Partial<TriggerCondition>) => {
    const conditions = value.conditions.map((condition, i) => {
      if (i !== index) return condition;
      const next = { ...condition, ...patch };
      if (next.kind !== 'on_dwell_time') next.dwellTimeMs = null;
      else if (next.dwellTimeMs === null) next.dwellTimeMs = DEFAULT_DWELL_MS;
      if (next.kind !== 'on_friction_detected') next.frictionSignal = null;
      else if (next.frictionSignal === null) next.frictionSignal = 'hesitation';
      return next;
    });
    onChange({ ...value, conditions });
  };

  return (
    <div className="trigger-editor">
      <p className="trigger-editor__lead">Conditions réunies (ET)</p>
      {value.conditions.map((condition, index) => (
        <div key={index} className="trigger-editor__row">
          <select
            aria-label={`Condition ${index + 1}`}
            value={condition.kind}
            onChange={(e) => setCondition(index, { kind: e.target.value as TriggerCondition['kind'] })}
          >
            {TRIGGER_KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {CONDITION_LABEL[kind]}
              </option>
            ))}
          </select>

          <select
            aria-label={`Cible de la condition ${index + 1}`}
            value={condition.targetSelector ?? ''}
            onChange={(e) => setCondition(index, { targetSelector: e.target.value || null })}
          >
            <option value="">L'étape entière</option>
            {/* A condition targets an element through its selector, in the visitor's browser: an
                element the measurement knew only by its label cannot be a target, the step is. */}
            {elements
              .filter((element) => element.selectors.length > 0)
              .map((element) => (
                <option key={element.selectors[0]} value={element.selectors[0]}>
                  {element.label ?? element.selectors[0]}
                </option>
              ))}
            {condition.targetSelector &&
              !elements.some((e) => e.selectors[0] === condition.targetSelector) && (
                <option value={condition.targetSelector}>{condition.targetSelector}</option>
              )}
          </select>

          {condition.kind === 'on_dwell_time' && (
            <label className="trigger-editor__inline">
              <span>Durée (s)</span>
              <input
                type="number"
                min={1}
                max={600}
                value={Math.round((condition.dwellTimeMs ?? DEFAULT_DWELL_MS) / 1000)}
                onChange={(e) => setCondition(index, { dwellTimeMs: Number(e.target.value) * 1000 })}
              />
            </label>
          )}

          {condition.kind === 'on_friction_detected' && (
            <select
              aria-label={`Signal de la condition ${index + 1}`}
              value={condition.frictionSignal ?? 'hesitation'}
              onChange={(e) =>
                setCondition(index, { frictionSignal: e.target.value as TriggerCondition['frictionSignal'] })
              }
            >
              {FRICTION_SIGNALS.map((signal) => (
                <option key={signal} value={signal}>
                  {SIGNAL_LABEL[signal]}
                </option>
              ))}
            </select>
          )}

          {value.conditions.length > 1 && (
            <Button
              variant="ghost"
              icon={<X />}
              aria-label={`Retirer la condition ${index + 1}`}
              onClick={() =>
                onChange({ ...value, conditions: value.conditions.filter((_, i) => i !== index) })
              }
            />
          )}
        </div>
      ))}
      {value.conditions.length < MAX_TRIGGER_CONDITIONS && (
        <Button
          variant="ghost"
          icon={<Plus />}
          onClick={() => onChange({ ...value, conditions: [...value.conditions, emptyCondition()] })}
        >
          Ajouter une condition
        </Button>
      )}

      <div className="trigger-editor__limits">
        <label className="trigger-editor__inline">
          <span>Affichages par session</span>
          <input
            type="number"
            min={1}
            max={10}
            value={value.maxDisplaysPerSession}
            onChange={(e) => onChange({ ...value, maxDisplaysPerSession: Number(e.target.value) })}
          />
        </label>
        <label className="trigger-editor__inline">
          <span>Composants par étape</span>
          <input
            type="number"
            min={1}
            max={5}
            value={value.maxComponentsPerStep}
            onChange={(e) => onChange({ ...value, maxComponentsPerStep: Number(e.target.value) })}
          />
        </label>
        <label className="trigger-editor__inline">
          <span>Délai (s)</span>
          <input
            type="number"
            min={0}
            max={30}
            value={Math.round(value.delayMs / 1000)}
            onChange={(e) => onChange({ ...value, delayMs: Number(e.target.value) * 1000 })}
          />
        </label>
      </div>
      <p className="trigger-editor__note">
        Les seuils de clics répétés et de clic sans effet sont contractuels et ne se règlent pas ici.
      </p>
    </div>
  );
}
